import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import ConfirmDialog from "../components/ConfirmDialog";

const ConfirmContext = createContext();

export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error("useConfirm must be used within a ConfirmProvider");
  }
  return context;
};

export const ConfirmProvider = ({ children }) => {
  const [options, setOptions] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "Confirm",
    cancelText: "Cancel",
    type: "confirm", // confirm, warning, error, info
    showCancel: true,
  });

  // We use a ref to store the resolve function of the current promise
  const resolveRef = useRef(null);

  const confirm = useCallback((dialogOptions = {}) => {
    return new Promise((resolve) => {
      setOptions({
        isOpen: true,
        title: dialogOptions.title || "Confirm",
        message: dialogOptions.message || "Are you sure?",
        confirmText: dialogOptions.confirmText || "Confirm",
        cancelText: dialogOptions.cancelText || "Cancel",
        type: dialogOptions.type || "confirm",
        showCancel: dialogOptions.showCancel ?? true,
      });
      resolveRef.current = resolve;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setOptions((prev) => ({ ...prev, isOpen: false }));
    if (resolveRef.current) {
      resolveRef.current(true);
      resolveRef.current = null;
    }
  }, []);

  const handleCancel = useCallback(() => {
    setOptions((prev) => ({ ...prev, isOpen: false }));
    if (resolveRef.current) {
      resolveRef.current(false);
      resolveRef.current = null;
    }
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog
        isOpen={options.isOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        title={options.title}
        message={options.message}
        confirmText={options.confirmText}
        cancelText={options.cancelText}
        type={options.type}
        showCancel={options.showCancel}
      />
    </ConfirmContext.Provider>
  );
};

